import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Pencil, Trash2, Save, X } from "lucide-react";
import { toast } from "sonner";
import { Field } from "./constants";
import { DateInput } from "./DateInput";

// Validation : la date début est requise pour persister la période
const isInhabSavable = (item) => !!(item && item.datedeb);

// Tronque toute date ISO `YYYY-MM-DDTHH:MM:SS` ou `YYYY-MM-DD` à `YYYY-MM-DD`
// pour l'affichage en liste — l'onglet Inhabilité ne montre que la date.
const fmtDate = (v) => (v ? String(v).slice(0, 10) : "");

/**
 * Onglet Inhabilité — édition manuelle (PAS d'autosave).
 *
 * Boutons : « Nouvelle période » (créer un brouillon), « Sauvegarder »,
 * « Annuler », « Supprimer ». Le panneau d'édition n'est affiché que lorsque
 * l'utilisateur clique explicitement sur « Modifier » ou « Nouvelle période ».
 */
export const InhabTab = ({ readOnly, inhabs, editInhab, setEditInhab, onSave, onDelete }) => {
    const [saving, setSaving] = useState(false);

    const handleSave = async () => {
        if (!isInhabSavable(editInhab)) {
            toast.error("La date de début est requise.");
            return;
        }
        setSaving(true);
        try {
            await onSave(editInhab);
            // Le parent (AvocatSheet) recharge la liste et ferme le panneau via setEditInhab(null)
            // si tout s'est bien passé — mais on ferme aussi côté local pour éviter l'incohérence.
            setEditInhab(null);
            toast.success(editInhab.id ? "Période modifiée" : "Période créée");
        } catch {
            // L'erreur est déjà affichée par onSave via toast
        } finally {
            setSaving(false);
        }
    };

    const handleCancel = () => setEditInhab(null);

    return (
        <div className="space-y-4">
            {!readOnly && (
                <Button
                    onClick={() => setEditInhab({ datedeb: "", datefin: "", comm: "" })}
                    className="rounded-md bg-[#0033A0] hover:bg-[#002277] text-white"
                    data-testid="add-inhab-btn"
                    disabled={!!editInhab}
                >
                    <Plus size={14} className="mr-2" /> Nouvelle période
                </Button>
            )}
            <div className="space-y-2">
                {inhabs.length === 0 ? (
                    <div className="text-sm text-slate-500 text-center py-8 border border-dashed border-slate-300 rounded-md">
                        Aucune période d&apos;inhabilité.
                    </div>
                ) : (
                    inhabs.map((it) => (
                        <div
                            key={it.id}
                            className="border border-slate-200 rounded-md p-3 flex items-start justify-between"
                            data-testid={`inhab-row-${it.id}`}
                        >
                            <div className="flex-1">
                                <div className="font-medium text-slate-900">
                                    {fmtDate(it.datedeb)} → {fmtDate(it.datefin) || "en cours"}
                                </div>
                                {it.comm && <div className="text-xs text-slate-600 mt-1">{it.comm}</div>}
                            </div>
                            {!readOnly && (
                                <div className="flex gap-1">
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        onClick={() => setEditInhab({ ...it, datedeb: fmtDate(it.datedeb), datefin: fmtDate(it.datefin) })}
                                        title="Modifier"
                                        disabled={!!editInhab}
                                        data-testid={`edit-inhab-${it.id}`}
                                    >
                                        <Pencil size={14} />
                                    </Button>
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        className="text-red-600"
                                        onClick={() => onDelete(it)}
                                        title="Supprimer"
                                        disabled={!!editInhab}
                                        data-testid={`delete-inhab-${it.id}`}
                                    >
                                        <Trash2 size={14} />
                                    </Button>
                                </div>
                            )}
                        </div>
                    ))
                )}
            </div>
            {editInhab && (
                <div className="border-2 border-[#0033A0] rounded-md p-4 space-y-3 bg-blue-50/30">
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                        <div className="font-semibold text-sm">
                            {editInhab.id ? "Modifier la période" : "Nouvelle période"}
                        </div>
                        <div className="flex items-center gap-2">
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={handleCancel}
                                className="rounded-md"
                                disabled={saving}
                                data-testid="cancel-inhab-btn"
                            >
                                <X size={14} className="mr-1" /> Annuler
                            </Button>
                            <Button
                                size="sm"
                                onClick={handleSave}
                                className="rounded-md bg-[#0033A0] hover:bg-[#002277] text-white"
                                disabled={!isInhabSavable(editInhab) || saving}
                                data-testid="save-inhab-btn"
                            >
                                <Save size={14} className="mr-1" />
                                {saving ? "Enregistrement…" : editInhab.id ? "Sauvegarder" : "Créer"}
                            </Button>
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <Field label="Date début">
                            <DateInput
                                value={editInhab.datedeb || ""}
                                onChange={(v) => setEditInhab({ ...editInhab, datedeb: v })}
                                className="rounded-md font-mono"
                                data-testid="inhab-datedeb"
                            />
                        </Field>
                        <Field label="Date fin">
                            <DateInput
                                value={editInhab.datefin || ""}
                                onChange={(v) => setEditInhab({ ...editInhab, datefin: v })}
                                className="rounded-md font-mono"
                                data-testid="inhab-datefin"
                            />
                        </Field>
                    </div>
                    <Field label="Commentaire">
                        <Textarea
                            value={editInhab.comm || ""}
                            onChange={(e) => setEditInhab({ ...editInhab, comm: e.target.value })}
                            rows={3}
                            className="rounded-md"
                            data-testid="inhab-comm"
                        />
                    </Field>
                    {!isInhabSavable(editInhab) && (
                        <p className="text-xs text-amber-700 italic">
                            &#9888; La date de début est requise pour sauvegarder la période.
                        </p>
                    )}
                </div>
            )}
        </div>
    );
};
